import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import LinearProgress from '@mui/material/LinearProgress';
import Popper from '@mui/material/Popper';
import type { SxProps, Theme } from '@mui/material/styles';
import { useTheme } from '@mui/material/styles';
import { type UseAutocompleteReturnValue } from '@mui/material/useAutocomplete';
import {
	Fragment,
	HTMLAttributes,
	ReactNode,
	useEffect,
	useLayoutEffect,
	useRef,
	useState
} from 'react';
import { useTranslation } from 'react-i18next';

import { POPPER_Z_INDEX } from '@/core/constants/layout';
import { mergeSx } from '@/utils/mergeSx';

import { DropdownItemBase } from '../autocomplete.types';

import { AutocompleteDropdownItem } from './AutocompleteDropdownItem/AutocompleteDropdownItem';
import { AutocompleteDropdownNoOptions } from './AutocompleteDropdownNoOptions/AutocompleteDropdownNoOptions';
import { autocompleteDropdownOptionsFilterStyles as sx } from './AutocompleteDropdownOptions.styles';

type AutoReturn<T> = UseAutocompleteReturnValue<T, false>;

type Props<T> = {
	open: boolean;
	loading?: boolean;
	query?: string;
	alreadySelected?: boolean;
	isAsync?: boolean;
	getListboxProps: AutoReturn<T>['getListboxProps'];
	getOptionProps: AutoReturn<T>['getOptionProps'];
	renderOption?: (
		option: T,
		optionProps: HTMLAttributes<HTMLLIElement>
	) => ReactNode;
	groupedOptions: AutoReturn<T>['groupedOptions'];
	options?: Array<T> | null;
	anchorEl: HTMLElement | null;
	customSx?: SxProps<Theme>;
	loadingText?: string;
	isTopOption?: (option: T) => boolean;
	sectionLabels?: { top: string; all: string };
	minWidth?: number;
	preserveScrollOnSelect?: boolean;
};

const VIEWPORT_MARGIN_PX = 16;

const buildSectionLabelsByIndex = <T extends DropdownItemBase>(
	groupedOptions: readonly T[],
	isTopOption?: (option: T) => boolean,
	sectionLabels?: { top: string; all: string }
): Map<number, string> => {
	const labelsByIndex = new Map<number, string>();
	if (!isTopOption || !sectionLabels) return labelsByIndex;

	const seenTopIds = new Set<string>();
	let hasTopSection = false;
	let allSectionIndex: number | undefined;

	groupedOptions.forEach((opt, idx) => {
		const isFirstTopOccurrence = isTopOption(opt) && !seenTopIds.has(opt.id);

		if (isFirstTopOccurrence) {
			seenTopIds.add(opt.id);
			if (idx === 0) hasTopSection = true;
			return;
		}

		if (allSectionIndex === undefined) allSectionIndex = idx;
	});

	if (hasTopSection && allSectionIndex !== undefined) {
		labelsByIndex.set(0, sectionLabels.top);
		labelsByIndex.set(allSectionIndex, sectionLabels.all);
	}

	return labelsByIndex;
};

export const AutocompleteDropdownOptions = <
	T extends DropdownItemBase = DropdownItemBase
>({
	open,
	loading,
	query,
	alreadySelected,
	isAsync,
	getListboxProps,
	getOptionProps,
	renderOption,
	groupedOptions,
	options,
	anchorEl,
	customSx,
	loadingText,
	isTopOption,
	sectionLabels,
	minWidth,
	preserveScrollOnSelect = false
}: Props<T>) => {
	const { t } = useTranslation();
	const theme = useTheme();
	const [popperWidth, setPopperWidth] = useState(anchorEl?.clientWidth ?? 200);
	// BasePopper calls its own popper.js instance's forceUpdate() on every
	// render (no deps) - bumping this keeps the popper glued to the anchor,
	// since MUI's own popperRef prop only captures a stale snapshot at mount.
	const [, forceRepositionTick] = useState(0);
	// ---------------------- scroll handling -----------------------------------
	// In case of async options, unless query is genuinely changed, we want to maintain the scroll position upon option selection
	const listboxNodeRef = useRef<HTMLUListElement | null>(null);
	const scrollTopRef = useRef(0);
	const previousQueryRef = useRef(query);
	const muiListboxRefRef = useRef<
		((node: HTMLUListElement | null) => void) | undefined
	>(undefined);

	const handleListboxScrollRef = useRef((event: Event) => {
		scrollTopRef.current = (event.currentTarget as HTMLUListElement).scrollTop;
	});

	const [setListboxRef] = useState(() => (node: HTMLUListElement | null) => {
		listboxNodeRef.current?.removeEventListener(
			'scroll',
			handleListboxScrollRef.current
		);
		listboxNodeRef.current = node;
		node?.addEventListener('scroll', handleListboxScrollRef.current, {
			passive: true
		});
		muiListboxRefRef.current?.(node);
	});

	const listboxProps = getListboxProps() as HTMLAttributes<HTMLUListElement> & {
		ref?: (node: HTMLUListElement | null) => void;
	};

	useLayoutEffect(() => {
		muiListboxRefRef.current = listboxProps.ref;
	}, [listboxProps.ref]);

	useEffect(() => {
		if (!isAsync || !preserveScrollOnSelect) return;

		const queryChanged = previousQueryRef.current !== query;
		previousQueryRef.current = query;
		if (queryChanged) return;

		const node = listboxNodeRef.current;
		if (!node) return;

		const savedScrollTop = scrollTopRef.current;
		const frame = window.requestAnimationFrame(() => {
			node.scrollTop = savedScrollTop;
		});
		return () => window.cancelAnimationFrame(frame);
	}, [groupedOptions, query, isAsync, preserveScrollOnSelect]);
	// ---------------------- scroll handling -----------------------------------

	useLayoutEffect(() => {
		if (!anchorEl) return;

		const observer = new ResizeObserver(entries => {
			setPopperWidth(entries[0].contentRect.width);
		});
		observer.observe(anchorEl);

		return () => {
			observer.disconnect();
		};
	}, [anchorEl]);

	// Anchor can move without resizing (e.g. a sibling filter's height
	// changing) - the ResizeObserver above won't catch that, so poll its
	// rect while open and reposition whenever it shifts.
	useEffect(() => {
		if (!open || !anchorEl) return;

		let rafId: number;
		let lastRect: DOMRect | null = null;

		const tick = () => {
			const rect = anchorEl.getBoundingClientRect();
			if (
				!lastRect ||
				rect.top !== lastRect.top ||
				rect.left !== lastRect.left ||
				rect.width !== lastRect.width ||
				rect.height !== lastRect.height
			) {
				lastRect = rect;
				forceRepositionTick(prevTick => prevTick + 1);
			}
			rafId = window.requestAnimationFrame(tick);
		};

		rafId = window.requestAnimationFrame(tick);
		return () => window.cancelAnimationFrame(rafId);
	}, [open, anchorEl]);

	if (!isAsync && !groupedOptions.length) return null;

	const sectionLabelsByIndex = buildSectionLabelsByIndex(
		groupedOptions as T[],
		isTopOption,
		sectionLabels
	);

	const isMobileLayout = window.innerWidth <= theme.breakpoints.values.md;
	const desiredWidth = Math.max(
		popperWidth,
		isMobileLayout ? 0 : (minWidth ?? 0)
	);
	const anchorLeft = anchorEl?.getBoundingClientRect().left ?? 0;
	const maxAvailableWidth = window.innerWidth - anchorLeft - VIEWPORT_MARGIN_PX;
	const width = Math.min(desiredWidth, maxAvailableWidth);

	return (
		<Popper
			open={open}
			anchorEl={anchorEl}
			placement='bottom-start'
			sx={{
				width,
				zIndex: POPPER_Z_INDEX
			}}
		>
			<Box sx={sx.dropdownWrapper}>
				<Box
					component='ul'
					{...listboxProps}
					ref={setListboxRef}
					sx={mergeSx(sx.dropdown, customSx)}
				>
					{isAsync &&
						groupedOptions.length === 0 &&
						(loading ? (
							<Box sx={sx.loadingText}>
								{loadingText ?? t('Loading options…')}
							</Box>
						) : (
							options !== null &&
							query &&
							query.length > 0 && (
								<AutocompleteDropdownNoOptions
									query={query}
									alreadySelected={alreadySelected}
								/>
							)
						))}

					{groupedOptions.map((opt, idx) => {
						const { key, ...optionProps } = getOptionProps({
							option: opt as T,
							index: idx
						});
						const sectionLabel = sectionLabelsByIndex.get(idx);

						return (
							<Fragment key={`${key}-${opt.id}`}>
								{sectionLabel && (
									<Box
										component='li'
										aria-hidden
										sx={mergeSx(
											sx.sectionLabel,
											idx !== 0 && sx.secondSectionLabel
										)}
									>
										{sectionLabel}
									</Box>
								)}
								{sectionLabel && (
									<Divider component='li' aria-hidden sx={sx.sectionDivider} />
								)}
								{renderOption ? (
									<Box component='li' {...optionProps}>
										{renderOption(opt as T, optionProps)}
									</Box>
								) : (
									<AutocompleteDropdownItem
										optionProps={optionProps}
										item={opt}
									/>
								)}
							</Fragment>
						);
					})}
				</Box>

				<LinearProgress
					sx={[sx.linearProgress, !loading && sx.linearProgressHidden]}
				/>
			</Box>
		</Popper>
	);
};
